import { getMode } from "@/modes";
import { selectActiveCadId } from "@/store/cad/cad-slice";
import { FeatureFlag } from "@/store/features/features";
import { selectModeName } from "@/store/mode-selectors";
import { selectActiveElementId } from "@/store/selections-selectors";
import { RootState } from "@/store/store";
import { GUID, assert } from "@faro-lotv/foundation";
import { ViewRuntimeContextData } from "../view-runtime-context";
import { DeepLink, getFullDeepLinkURL } from "./deep-link-encoding";

/**
 * @param state The current state of the app
 * @param view information of the current app 3d view
 * @param lookAtId an optional element o look at
 * @returns the URL for the generate deep link
 */
export function computeDeepLink(
  state: RootState,
  view: ViewRuntimeContextData,
  lookAtId?: GUID,
): URL {
  const id = selectActiveElementId(state);
  assert(id, "Can't create a deep link without an active element");
  const cadId = selectActiveCadId(state);
  const mode = selectModeName(state);
  const initialState = getMode(mode).initialState?.compute(state, view) ?? {};

  const linkData: DeepLink & Record<string, unknown> = {
    ...initialState,
    id,
    mode,
    ...(cadId ? { cadId } : {}),
    ...(lookAtId ? { lookAtId } : {}),
  };

  return getFullDeepLinkURL(linkData);
}

/**
 * @returns a deep link without any hardcoded context. This way the viewer can decide how to display the element.
 * @param lookAtId The element that should be shown
 */
export function computeLookAtDeepLink(lookAtId: GUID): URL {
  return getFullDeepLinkURL({
    lookAtId,
  });
}

/**
 * Adds a feature flag to an existing URL.
 *
 * @param url The URL to update
 * @param featureFlag The feature flag to add
 * @returns The updated URL with the feature flag added.
 */
export function addFeatureFlagToUrl(url: URL, featureFlag: FeatureFlag): URL {
  if (!url.searchParams.has(featureFlag)) {
    url.searchParams.append(featureFlag, "");
  }
  return url;
}

/**
 * Removes the provided URL search parameter from the current URL.
 * This is useful when a component handles a parameter on page load, and it's then no longer needed elsewhere.
 *
 * @param param URL search parameter.
 */
export function removeSearchParam(param: string): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  try {
    window.history.replaceState({}, "", url.toString());
  } catch {
    console.error("Failed to update browser history!");
  }
}
